'use client';
import React, { useRef } from 'react';
import ButtonGroupItem from '@/components/ui/ButtonGroupItem/ButtonGroupItem';
import LinkButton from '@/components/ui/LinkButton/LinkButton';
import CompactButton from '@/components/ui/CompactButton/CompactButton';
import PrimaryButton from '@/components/ui/PrimaryButton/PrimaryButton';
import { LanguageType, LanguageDirection, BrandInfoSectionContent } from '@/lib/types';
import { usePathname } from 'next/navigation';
import { useAnimatedNavigate } from '@/hooks/useAnimatedNavigation';
import { RIVIVE_LOCATION_LINK } from '@/lib/variables';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { useLenis } from '@/contexts/LenisContext';
import useAspectRatio from '@/hooks/useAspectRatio';

import { debug } from '@/lib/debug';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);


interface FooterSectionProps {
  language: LanguageType;
  setLanguage: (language: string) => void;
  footerRef?: React.RefObject<HTMLDivElement | null>;
  ctaSectionRef?: React.RefObject<HTMLElement | null>;
  brandInfoSectionPageData: BrandInfoSectionContent;
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

// Map service display names to navigationLabel IDs
const SERVICE_NAME_TO_NAVIGATION_LABEL: Record<string, string> = {
  // English service names
  'Rivive Signature Evaluation': 'Evaluation',
  'Facial Vitality Glow': 'Face',
  'Complete Body Renewal': 'Body',
  'Vital Essence Rejuvenation': 'Rejuvenation',
  // Arabic service names
  'تقييم ريڤيڤ سيجنتشر': 'تقييم',
  'إشراقة حيوية للوجه': 'الوجه',
  'تجديد كامل للجسم': 'الجسم',
  'تجديد البشرة بخلاصة حيوية': 'التجديد',
};

const FooterSection: React.FC<FooterSectionProps> = ({
  language,
  setLanguage,
  footerRef,
  ctaSectionRef,
  brandInfoSectionPageData
}) => {
  const isLg = useIsLg();
  const isTb = useIsTb();
  const pathname = usePathname();
  const navigate = useAnimatedNavigate();
  const sunRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const aspectRatio = useAspectRatio();
  const { lenis } = useLenis();

  // debug.log('footer section rerender');

  useGSAP(() => {
    // if (!sunRef.current || !footerRef?.current || !ctaSectionRef?.current || !lineRef.current) return;

    const ctaSection = ctaSectionRef?.current;
    const footer = footerRef?.current;
    const sun = sunRef.current;
    const line = lineRef.current;
    // if (!ctaSection || !footer || !sun || !line) return;

    if (isLg) {
      if (ctaSection && footer && sun && line) {
        gsap.set(footer, {
          // opacity: 0.5,
          yPercent: -25,
          // visibility: 'hidden',
        });

        gsap.to(footer, {
          // opacity: 1,
          yPercent: 0,
          scrollTrigger: {
            trigger: ctaSection,
            start: "bottom bottom",
            // end: "bottom 25%",
            // end: `+=${footer.offsetHeight}px`,
            end: "bottom top",
            scrub: true,
            // markers: true,
            id: 'footer-animation-parallax',
            invalidateOnRefresh: true,
            onEnter: () => {
              footer.style.willChange = 'transform';
            },
            onEnterBack: () => {
              footer.style.willChange = 'auto';
            },
            onLeave: () => {
              footer.style.willChange = 'transform';
            },
            onLeaveBack: () => {
              footer.style.willChange = 'auto';
            }
          }
        });


        gsap.to(sun, {
          yPercent: -50,
          scale: 1,
          ease: "none",
          scrollTrigger: {
            id: 'sun-animation-parallax',
            invalidateOnRefresh: true,
            trigger: line,
            start: () => `top+=${footer!.offsetHeight * 0.25}px bottom`,
            endTrigger: footer,
            end: () => `bottom+=${footer!.offsetHeight * 0.25}px bottom`,
            scrub: true,
            // markers: true,
          }
        });
      }
    } else {

      if (footer && sun && line) {
        gsap.to(sun, {
          yPercent: -50,
          scale: 1,
          ease: "none",
          scrollTrigger: {
            trigger: line,
            start: "top bottom",
            endTrigger: footer,
            end: "bottom-=5% bottom",
            scrub: true,
            invalidateOnRefresh: true,
            // markers: true,
          }
        });
      }
    }

  }, { dependencies: [ctaSectionRef, isLg, isTb, aspectRatio], revertOnUpdate: true }); // TODO: ??
  // }, { dependencies: [], revertOnUpdate: true }); // TODO: ??
  // },  []); // TODO: ??

  const handleFAQClick = () => {
    const isHomeOrContact = pathname === '/' || pathname === '/contact';

    if (isHomeOrContact) {
      // document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
      const faq = document.getElementById('faq');
      if (faq) {
        const yOffset = -window.innerHeight / 2 + faq?.getBoundingClientRect().height / 2;
        lenis?.scrollTo(faq, { offset: yOffset, immediate: false, duration: 1.5 });
      }
    } else {
      navigate('/', { state: { scrollToFAQ: true } }); // TODO: currently not working. to be fixed
    }
  };

  const handleReferralClick = () => {
    const isSignUpPage = pathname === '/signup';

    if (isSignUpPage) {
      const referralSection = document.getElementById('referral-section');
      if (referralSection) {
        const yOffset = isLg ? -100 : -50;
        const y = referralSection.getBoundingClientRect().top + window.scrollY + yOffset;
        lenis?.scrollTo(y, { offset: 0, immediate: false, duration: 1.5 });
      }
    } else {
      navigate('/signup', { state: { scrollToReferral: true } });
    }
  };

  const handleServiceClick = (serviceName: string) => {
    const navigationLabel = SERVICE_NAME_TO_NAVIGATION_LABEL[serviceName];
    if (!navigationLabel) return;

    const isServicesPage = pathname === '/services';

    if (isServicesPage) {
      const serviceSection = document.getElementById(navigationLabel);
      if (serviceSection) {
        const yOffset = isLg ? -100 : -50;
        const y = serviceSection.getBoundingClientRect().top + window.scrollY + yOffset;
        lenis?.scrollTo(y, { offset: 0, immediate: false, duration: 1.5 });
      }
    } else {
      navigate('/services', { state: { scrollToService: true, serviceNavigationLabel: navigationLabel } });
    }
  };

  return (
    <footer ref={footerRef} className='
        footer-section
        w-full
        px-[1.25rem]
        padding-main-x-desktop
        pb-[1.5rem]
        tb:pb-0
        flex items-center justify-center
        '
      style={{
        // display: isLg ? 'none' : 'flex',
        display: 'flex',
        // position: isLg ? 'fixed' : 'relative',
        // bottom: 0,
        // left: 0,
        // right: 0,
      }}
    >

      <div className='
           w-full max-w-full container-large
           flex flex-col gap-[2.5rem] lg:gap-[7rem]
          '>

        <div className='
            flex flex-col
            w-full
            gap-y-[2.5rem]
            lg:gap-y-[6rem]
            '>

          <div
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            className='
                w-full
                flex flex-col lg:flex-row 
                gap-[2.5rem]
                lg:gap-x-[4rem]
                lg:gap-y-[3.5rem]
                items-start
                lg:items-end
              '>
            <div className='lg:w-[24.3125rem] flex-grow lg:max-w-[37rem]'>
              <img src="/Logomark-Registration-Small.svg" alt="Logotype wide" className="h-[4.5rem] lg:h-[6.25rem]" />
            </div>

            <div className='lg:min-w-[37rem] flex-grow  flex flex-row  justify-end'>
              <div className=' 
                  lg:max-w-[56rem]
                  flex flex-row flex-grow flex-wrap
                  lg:gap-x-[1rem] lg:gap-y-[1.5rem]
                  '>
                <div className='
                    flex flex-row flex-wrap
                    gap-x-[1.25rem] gap-y-[1rem] lg:gap-x-[2.5rem] lg:gap-y-[1.5rem]
                    items-start lg:items-center lg:justify-start
                    w-full
                    lg:flex-grow
                    lg:w-[24.3125rem]
                    lg:max-w-[37rem]
                    '>
                  <LinkButton
                    text={language === 'arabic' ? 'الرئيسية' : 'Home'}
                    size={isTb ? 28 : 24}
                    onClick={() => navigate('/')}
                    disabled={pathname === '/'}
                    language={language}
                  />
                  <LinkButton
                    text={language === 'arabic' ? 'عننا' : 'About Us'}
                    size={isTb ? 28 : 24}
                    onClick={() => navigate('/about')}
                    disabled={pathname === '/about'}
                    language={language}
                  />
                  <LinkButton
                    text={language === 'arabic' ? 'الخدمات' : 'Services'}
                    size={isTb ? 28 : 24}
                    onClick={() => navigate('/services')}
                    disabled={pathname === '/services'}
                    language={language}
                  />
                  <LinkButton
                    text={language === 'arabic' ? 'تواصل معنا' : 'Contact'}
                    size={isTb ? 28 : 24}
                    onClick={() => navigate('/contact')}
                    disabled={pathname === '/contact'}
                    language={language}
                  />
                </div>
                {isLg &&
                  <div className='flex flex-row w-[18rem] gap-[0.75rem] items-center justify-start'>
                    {/* <CompactButton size={56}>
                      <User />
                    </CompactButton> */}
                    <div className='w-fit'>
                      <PrimaryButton 
                        size={56} 
                        text={language === 'arabic' ? 'انضمي لريڤيڤ' : 'Join Rivive'} 
                        type='accent' 
                        onClick={() => {
                          // Check if we're on the signup page
                          if (pathname === '/signup') {
                            const section = document.getElementById('signup-section');
                            if (section) {
                              // Emit event to hide navbar during smooth scroll
                              window.dispatchEvent(new CustomEvent('lenisScrollStart'));

                              const y = section.getBoundingClientRect().top + window.scrollY;

                              lenis?.scrollTo(y, {
                                offset: 0,
                                duration: 2,
                                easing: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
                                immediate: false,
                                lock: true,
                                onComplete: () => {
                                  // Re-enable user scroll and navbar
                                  window.dispatchEvent(new CustomEvent('lenisScrollEnd'));
                                }
                              });
                            }
                          } else {
                            navigate('/signup');
                          }
                        }} 
                        language={language} 
                      />
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>

          <div
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            className='
                flex flex-col lg:flex-row
                w-full
                gap-[2.5rem]
                lg:gap-x-[4rem]
                lg:gap-y-[1.5rem]
              '>

            <div className='
                  flex flex-col
                  gap-[1.5rem]
                  lg:gap-[2rem]
                  lg:w-[24.3125rem] lg:flex-grow  lg:max-w-[37rem]
                  '>
              <p className={`
                  max-w-[30.6875rem]
                  text-text-primary
                  ${isTb ?
                  'text-36-to-24--neue-june'
                  :
                  'text-24-fixed--neue-june'
                }
                `}
                style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                {language === 'arabic' ? "جوهر الجمال" : "Essence of beauty"}
              </p>

              <div className='flex flex-row flex-wrap  gap-[1rem] w-full'>
                <CompactButton size={isLg ? 56 : 44} radius={9999} radialFill={true} onClick={() => window.open(brandInfoSectionPageData.instagramLink, '_blank')}>
                  <img src="/Brand-Instagram.svg" alt="Instagram icon" className="w-full z-10" />
                </CompactButton>
                <CompactButton size={isLg ? 56 : 44} radius={9999} radialFill={true} onClick={() => window.open(brandInfoSectionPageData.facebookLink, '_blank')}>
                  <img src="/Brand-Facebook.svg" alt="Facebook icon" className="w-full z-10" />
                </CompactButton>
                <CompactButton size={isLg ? 56 : 44} radius={9999} radialFill={true} onClick={() => window.open(brandInfoSectionPageData.tiktokLink, '_blank')}>
                  <img src="/Brand-Tiktok.svg" alt="Tiktok icon" className="w-full z-10" />
                </CompactButton>
                <CompactButton size={isLg ? 56 : 44} radius={9999} radialFill={true} onClick={() => window.open(brandInfoSectionPageData.linkedinLink, '_blank')}>
                  <img src="/Brand-Linkedin.svg" alt="Linkedin icon" className="w-full z-10" />
                </CompactButton>
                <CompactButton size={isLg ? 56 : 44} radius={9999} radialFill={true} onClick={() => window.open(brandInfoSectionPageData.snapchatLink, '_blank')}>
                  <img src="/Brand-Snapchat.svg" alt="Snapchat icon" className="w-full z-10" />
                </CompactButton>
              </div>
            </div>


            <div className='flex flex-row justify-end lg:min-w-[37rem] flex-grow '>
              <div className=' lg:max-w-[56rem] flex-grow flex flex-row flex-wrap gap-x-[0.75rem] lg:gap-x-[1rem] gap-y-[2rem] items-start justify-start '>
                <div className='flex flex-col gap-[1.5rem] items-start w-[10rem] max-w-full flex-grow lg:w-[13.6875rem] lg:flex-grow lg:max-w-full'> {/* TODO: design uses 11.6875 rem here but using 13.6875 seems to wrap and fit well*/}
                  <LinkButton size={isLg ? 28 : 24} text={language === 'arabic' ? "العلاجات" : "Services"} onClick={() => navigate('/services')} language={language} />

                  <div className='flex flex-col gap-[0.75rem] items-start'>
                    <LinkButton 
                      size={isLg ? 18 : 16} 
                      text={language === 'arabic' ? "تقييم ريڤيڤ سيجنتشر" : "Rivive Signature Evaluation"} 
                      language={language}
                      onClick={() => handleServiceClick(language === 'arabic' ? "تقييم ريڤيڤ سيجنتشر" : "Rivive Signature Evaluation")}
                    />
                    <LinkButton 
                      size={isLg ? 18 : 16} 
                      text={language === 'arabic' ? "إشراقة حيوية للوجه" : "Facial Vitality Glow"} 
                      language={language}
                      onClick={() => handleServiceClick(language === 'arabic' ? "إشراقة حيوية للوجه" : "Facial Vitality Glow")}
                    />
                    <LinkButton 
                      size={isLg ? 18 : 16} 
                      text={language === 'arabic' ? "تجديد كامل للجسم" : "Complete Body Renewal"} 
                      language={language}
                      onClick={() => handleServiceClick(language === 'arabic' ? "تجديد كامل للجسم" : "Complete Body Renewal")}
                    />
                    <LinkButton 
                      size={isLg ? 18 : 16} 
                      text={language === 'arabic' ? "تجديد البشرة بخلاصة حيوية" : "Vital Essence Rejuvenation"} 
                      language={language}
                      onClick={() => handleServiceClick(language === 'arabic' ? "تجديد البشرة بخلاصة حيوية" : "Vital Essence Rejuvenation")}
                    />
                  </div>
                </div>

                <div className='flex flex-col gap-[1.5rem] items-start w-[10rem] max-w-full flex-grow lg:w-[13.6875rem] lg:flex-grow lg:max-w-full '>
                  <LinkButton size={isLg ? 28 : 24} text={language === 'arabic' ? "المتجر" : "Shop"} language={language} />

                  <div className='flex flex-col gap-[0.75rem] items-start'>
                    <LinkButton size={isLg ? 18 : 16} text={language === 'arabic' ? "قريبًا" : "Coming soon"} disabled={true} language={language} />
                  </div>
                </div>
                <div className='flex flex-col gap-[1.5rem] items-start w-[10rem] max-w-full flex-grow lg:w-[13.6875rem] lg:flex-grow lg:max-w-full '>
                  <LinkButton size={isLg ? 28 : 24} text={language === 'arabic' ? "الدعم" : "Support"} language={language} onClick={() => navigate('/contact')}/>

                  <div className='flex flex-col gap-[0.75rem] items-start'>
                    <LinkButton
                      size={isLg ? 18 : 16}
                      text={
                        language === 'arabic'
                          ? "لومير سنتر، حي حطين\nRRHA8736، الرياض، السعودية"
                          : "Lumière Center, Hittin\nRRHA8736, Riyadh, SA"
                      }
                      language={language}
                      onClick={() => window.open(RIVIVE_LOCATION_LINK, '_blank')}
                    /> {/* // TODO: this should be fetched from strapi instead */}
                    <LinkButton
                      size={isLg ? 18 : 16}
                      text={language === 'arabic' ? "تلفون: ٠١١٥١٠٦٦٩٩" : `T: ${brandInfoSectionPageData.mainTelephoneNumber}`}
                      language={language}
                      onClick={() => window.open(`tel:${brandInfoSectionPageData.mainTelephoneNumber}`, '_self')}
                    />
                    <LinkButton
                      size={isLg ? 18 : 16}
                      text={language === 'arabic' ? `الايميل: ${brandInfoSectionPageData.mainEmailAddress}` : `E: ${brandInfoSectionPageData.mainEmailAddress}`}
                      language={language}
                      onClick={() => window.open(`mailto:${brandInfoSectionPageData.mainEmailAddress}`, '_self')}
                    />
                    <LinkButton
                      size={isLg ? 18 : 16}
                      text={language === 'arabic' ? "الأسئلة الشائعة" : "FAQs"}
                      onClick={handleFAQClick}
                      language={language}
                    />
                    <LinkButton
                      size={isLg ? 18 : 16}
                      text={language === 'arabic' ? "إحالة صديق" : "Refer-a-Friend"}
                      language={language}
                      onClick={handleReferralClick}
                    />
                  </div>
                </div>
              </div>
            </div>



          </div>

        </div>

        <div
          style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
          className='
              flex flex-col
              gap-y-[3.5rem]
              lg:gap-y-[4.5rem]
            '>
          <div className='
              flex flex-col 
              gap-[2rem]
              '>
            <div className='
                flex flex-col-reverse lg:flex-row items-end justify-between
                gap-[2rem] lg:gap-[1rem]
                pb-[4.5rem]
                lg:pb-0
                '>

              <div className='w-full lg:w-[24.3125rem] lg:max-w-[37rem] items-center justify-center lg:items-start lg:justify-normal flex flex-col gap-[1rem]'>
                <p className='text-16--alexandria text-text-primary'>
                  {language === 'arabic' ? 'شركاء التقنية:' : 'Technology Partners:'}
                </p>
                <img src="/TechPartnerBadgeTamara.svg" onClick={() => window.open('https://tamara.co/ar-sa', '_blank')} alt="Tamara technology partner badge" className="w-[6.25rem] cursor-pointer" />
              </div>

              <div className='w-full lg:min-w-[24.3125rem] flex items-center justify-center lg:justify-end lg:items-end'>
                <div className='flex flex-col lg:flex-row gap-[0.75rem] items-center'>
                  <p className='text-16--alexandria text-text-primary'>
                    {language === 'arabic' ? 'اللغه' : 'Language'}:
                  </p>
                  <div className='flex flex-row gap-[0.5rem] lg:gap-[0.25rem]'>
                    <ButtonGroupItem text="English" itemType='narrow' size={16} onClick={() => setLanguage('EN')} isSelected={language === 'english'} />
                    <ButtonGroupItem text="العربيه" itemType='narrow' size={16} onClick={() => setLanguage('AR')} isSelected={language === 'arabic'} />
                  </div>
                </div>
              </div>
            </div>

            <div className="relative pointer-events-none">
              <div ref={lineRef} className="h-[1.5px] bg-stroke-soft-200 relative z-10"></div>

              <div className="
                  absolute 
                  top-[-21.875rem] 
                  lg:top-[-25rem] 
                  left-1/2 
                  -translate-x-1/2
                  max-w-full
                  w-[25rem] 
                  h-[21.875rem] 
                  lg:w-[31.25rem] 
                  lg:h-[25rem] 
                  overflow-hidden
                  flex items-center justify-center
                  z-10
                ">
                <div
                  ref={sunRef}
                  className='
                      w-[6.5rem] 
                      h-[6.5rem] 
                      lg:w-[13rem] 
                      lg:h-[13rem] 
                      rounded-full 
                      scale-75
                      bg-theme-yellow-primary-base
                      shadow-sunshine-wide-glow
                      absolute
                      bottom-[-6.5rem]
                      lg:bottom-[-13rem]
                    '
                />
              </div>
            </div>

            <div className='
                flex flex-col lg:flex-row items-center justify-between
                gap-[1rem]
                z-10
              '>
              <div className='text-16--alexandria text-text-primary'>
                <p
                  style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                  className='text-16--alexandria text-text-primary whitespace-nowrap'>
                  {language === 'arabic' ? `© ${new Date().getFullYear()} ريڤيڤ. جميع الحقوق محفوظة.` : `© ${new Date().getFullYear()} Rivive. All rights reserved.`}
                </p>
              </div>

              <div className='w-full flex items-center justify-center lg:justify-end'>
                <div
                  style={{ direction: "ltr" }}
                  className='flex flex-row flex-wrap lg:flex-row gap-[1rem] lg:gap-[2rem] items-center justify-center'>
                  <LinkButton text={language === 'arabic' ? 'الشروط والأحكام' : 'Terms & Conditions'} size={16} onClick={() => navigate('/legal')} language={language} />
                  <LinkButton text={language === 'arabic' ? 'سياسة الخصوصيه' : 'Privacy Policy'} size={16} onClick={() => navigate('/legal')} language={language} />
                  <LinkButton text={language === 'arabic' ? 'سياسة ملفات تعريف الإرتباط' : 'Cookies Policy'} size={16} onClick={() => navigate('/legal')} language={language} />
                </div>
              </div>
            </div>
          </div>

          <div className='w-full'>
            <img src="/Logotype-Wide.svg" alt="Logotype wide" className="w-full" />
          </div>
        </div>

      </div>

    </footer>
  );
};

export default FooterSection; 
